import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  fmtKr,
  fmtMaaned,
  fmtTid,
  isoDato,
  type Me,
  type Person,
  type SessionRow,
  type SummaryRow,
} from '../lib/api.ts';
import { Field, inputKlasse, knappSekundær, Modal, knappPrimær } from '../components/ui.tsx';

const GRUPPERINGER = [
  ['person', 'Person'],
  ['group', 'Økonomigruppe'],
  ['charger', 'Lader'],
  ['month', 'Måned'],
] as const;

export default function Economy({ me }: { me: Me }) {
  const [fra, setFra] = useState(() => isoDato(new Date(Date.now() - 29 * 86_400_000)));
  const [til, setTil] = useState(() => isoDato(new Date()));
  const [groupBy, setGroupBy] = useState<string>('person');
  const [flyttOkt, setFlyttOkt] = useState<SessionRow | null>(null);

  const qs = `from=${fra}&to=${til}`;
  const summaryQuery = useQuery<SummaryRow[]>({
    queryKey: ['economy-summary', fra, til, groupBy],
    queryFn: () => api.get(`/economy/summary?${qs}&groupBy=${groupBy}`),
  });
  const sessionsQuery = useQuery<SessionRow[]>({
    queryKey: ['economy-sessions', fra, til],
    queryFn: () => api.get(`/economy/sessions?${qs}`),
  });

  const rows = summaryQuery.data ?? [];
  const sum = rows.reduce(
    (acc, r) => ({
      kwh: acc.kwh + Number(r.kwh),
      kost: acc.kost + Number(r.kostTotal),
      okter: acc.okter + r.antOkter,
      uberegnet: acc.uberegnet + r.uberegnet,
    }),
    { kwh: 0, kost: 0, okter: 0, uberegnet: 0 },
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-3">
        <h1 className="mr-auto text-xl font-bold text-slate-800">Økonomi</h1>
        <Field label="Fra">
          <input type="date" value={fra} onChange={(e) => setFra(e.target.value)} className={inputKlasse} />
        </Field>
        <Field label="Til">
          <input type="date" value={til} onChange={(e) => setTil(e.target.value)} className={inputKlasse} />
        </Field>
        <a href={`/api/v1/economy/export?${qs}`} className={knappSekundær}>
          Eksporter CSV
        </a>
      </div>

      <div className="flex gap-1">
        {GRUPPERINGER.map(([verdi, navn]) => (
          <button
            key={verdi}
            onClick={() => setGroupBy(verdi)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
              groupBy === verdi ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 ring-1 ring-slate-200'
            }`}
          >
            {navn}
          </button>
        ))}
      </div>

      <div className="overflow-x-auto rounded-xl bg-white ring-1 ring-slate-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
              <th className="px-4 py-3">{GRUPPERINGER.find(([v]) => v === groupBy)?.[1]}</th>
              <th className="px-4 py-3 text-right">Økter</th>
              <th className="px-4 py-3 text-right">kWh</th>
              <th className="px-4 py-3 text-right">Strøm</th>
              <th className="px-4 py-3 text-right">Nettleie</th>
              <th className="px-4 py-3 text-right">Totalt</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key ?? 'ukjent'} className="border-b border-slate-100 last:border-0">
                <td className="px-4 py-2 font-medium text-slate-700">
                  {groupBy === 'month' ? fmtMaaned(r.navn) : r.navn}
                </td>
                <td className="px-4 py-2 text-right">{r.antOkter}</td>
                <td className="px-4 py-2 text-right">{Number(r.kwh).toFixed(1)}</td>
                <td className="px-4 py-2 text-right">{fmtKr(r.kostStrom)}</td>
                <td className="px-4 py-2 text-right">{fmtKr(r.kostNett)}</td>
                <td className="px-4 py-2 text-right font-semibold">{fmtKr(r.kostTotal)}</td>
              </tr>
            ))}
            {rows.length > 0 && (
              <tr className="bg-slate-50 font-semibold text-slate-800">
                <td className="px-4 py-2">Sum</td>
                <td className="px-4 py-2 text-right">{sum.okter}</td>
                <td className="px-4 py-2 text-right">{sum.kwh.toFixed(1)}</td>
                <td className="px-4 py-2 text-right" colSpan={2}></td>
                <td className="px-4 py-2 text-right">{fmtKr(sum.kost)}</td>
              </tr>
            )}
            {!rows.length && !summaryQuery.isLoading && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-slate-500">
                  Ingen økter i perioden.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {sum.uberegnet > 0 && (
        <p className="text-xs text-amber-600">
          {sum.uberegnet} økt(er) mangler kostnadsberegning (prisdata hentes automatisk).
        </p>
      )}

      <div>
        <h2 className="mb-2 text-sm font-semibold text-slate-700">Enkeltøkter</h2>
        <div className="overflow-x-auto rounded-xl bg-white ring-1 ring-slate-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
                <th className="px-4 py-3">Start</th>
                <th className="px-4 py-3">Lader</th>
                <th className="px-4 py-3">Person</th>
                <th className="px-4 py-3 text-right">kWh</th>
                <th className="px-4 py-3 text-right">Totalt</th>
                {(me.rolle === 'admin' || me.rolle === 'sysadmin') && <th className="px-4 py-3"></th>}
              </tr>
            </thead>
            <tbody>
              {(sessionsQuery.data ?? []).map((s) => (
                <tr key={s.id} className="border-b border-slate-100 last:border-0">
                  <td className="px-4 py-2">{fmtTid(s.startTs)}</td>
                  <td className="px-4 py-2">{s.laderNavn}</td>
                  <td className="px-4 py-2">
                    {s.personNavn ?? '–'}
                    {s.manueltFlyttet && <span className="ml-1 text-xs text-amber-600" title="Manuelt flyttet">✱</span>}
                  </td>
                  <td className="px-4 py-2 text-right">{Number(s.kwhTotal).toFixed(1)}</td>
                  <td className="px-4 py-2 text-right">{fmtKr(s.kostTotal)}</td>
                  {(me.rolle === 'admin' || me.rolle === 'sysadmin') && (
                    <td className="px-4 py-2 text-right">
                      <button onClick={() => setFlyttOkt(s)} className="text-xs text-blue-600 hover:underline">
                        Flytt
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {flyttOkt && <FlyttModal okt={flyttOkt} onClose={() => setFlyttOkt(null)} />}
    </div>
  );
}

function FlyttModal({ okt, onClose }: { okt: SessionRow; onClose: () => void }) {
  const qc = useQueryClient();
  const [personId, setPersonId] = useState(okt.personId ?? '');
  const [feil, setFeil] = useState<string | null>(null);
  const personsQuery = useQuery<Person[]>({
    queryKey: ['persons'],
    queryFn: () => api.get('/persons'),
  });

  const flytt = useMutation({
    mutationFn: () => api.post(`/economy/sessions/${okt.id}/reassign`, { personId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['economy-sessions'] });
      qc.invalidateQueries({ queryKey: ['economy-summary'] });
      onClose();
    },
    onError: (err) => setFeil(err instanceof Error ? err.message : 'Feil'),
  });

  return (
    <Modal tittel="Flytt økt til annen person" onClose={onClose}>
      <p className="mb-3 text-sm text-slate-600">
        Økt {fmtTid(okt.startTs)} på {okt.laderNavn} ({Number(okt.kwhTotal).toFixed(1)} kWh).
        Kostnaden reberegnes mot den nye personens strømavtale.
      </p>
      <Field label="Ny person">
        <select value={personId} onChange={(e) => setPersonId(e.target.value)} className={inputKlasse}>
          <option value="">– velg –</option>
          {(personsQuery.data ?? [])
            .filter((p) => p.aktiv)
            .map((p) => (
              <option key={p.id} value={p.id}>
                {p.navn}
              </option>
            ))}
        </select>
      </Field>
      {feil && <p className="mt-2 text-sm text-red-600">{feil}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onClose} className={knappSekundær}>
          Avbryt
        </button>
        <button onClick={() => flytt.mutate()} disabled={!personId || flytt.isPending} className={knappPrimær}>
          Flytt økt
        </button>
      </div>
    </Modal>
  );
}
